import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import apiCall from "shared/services/ApiAdapter";
import ContentHeaderSection from "shared/common/ContentHeaderSection";
import { useMasterList } from "shared/hooks/useMasterList";
import { getCurrentUserId } from "shared/utils/SessionDataUtils";

const CreateUpdateEvaluationForm = ({
  navigateTo,
  evaluationId,
  useMockData,
  displayToast,
  courseId, // New prop for preselecting a course
}) => {
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  const userId = getCurrentUserId();

  const {
    masterData: {
      curricula,
      academicClasses,
      subjects,
      chapters,
      questionTypes,
      tags,
    },
    isLoading: masterDataLoading,
    error: masterDataError,
    clearCache,
  } = useMasterList({ useMockData, displayToast, startLoading, stopLoading });

  // Helper function to get code from ID for subject or academic class
  const getCodeFromId = (id, items) => {
    if (!id) return "";
    // If it's already a code (matches an item.value), return it
    const foundByValue = items?.find((item) => item.value === id);
    if (foundByValue) return id;
    // Otherwise, try to find by ID and return the code
    const foundById = items?.find((item) => String(item.id) === String(id));
    return foundById?.value || id;
  };

  const [formLoading, setFormLoading] = useState(false);
  const [chaptersError, setChaptersError] = useState(null);
  const [courses, setCourses] = useState([]);

  const [evaluation, setEvaluation] = useState({
    name: "",
    courseValue: "",
    chapterValue: [],
    curriculumValue: "",
    classStandardValue: "",
    subjectValue: "",
    difficulty: [],
    questionTypes: [],
    tags: [],
    conceptTopic: "",
    numberOfQuestions: "",
    maxTimeMinutes: "",
    expiryDateTime: "",
    isPractice: false,
    durationSeconds: "",
    isActive: true,
  });

  const todayLocalISO = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const tzOffset = now.getTimezoneOffset();
    const localMidnight = new Date(now.getTime() - tzOffset * 60000);
    return localMidnight.toISOString().split("T")[0];
  }, []);


  useEffect(() => {
    clearCache(false);
  }, [clearCache]);

  const toValue = (item) => {
    if (!item) return "";
    if (typeof item === "string" || typeof item === "number") return item;
    return item.value ?? item.id ?? "";
  };

  const isInitialLoadRef = useRef(true);

  const getCourses = async () => {
    try {
      const userId = getCurrentUserId();
      const data = await apiCall(
        `/courses?isActive.equals=true&createdBy.equals=${userId}&size=10000`,
        useMockData,
        "GET",
        null,
        startLoading,
        stopLoading,
        displayToast
      );
      setCourses(data || []);
      return data || [];
    } catch (error) {
      console.error("Failed to fetch courses:", error);
      return [];
    }
  };

  // Consolidated useEffect to handle fetching and pre-population
  useEffect(() => {
    const initializeForm = async () => {
      setFormLoading(true);
      const fetchedCourses = await getCourses();

      if (evaluationId) {
        // Fetch and populate data for an existing evaluation
        try {
          const data = await apiCall(
            `/evaluations/${evaluationId}`,
            useMockData,
            "GET",
            { id: evaluationId },
            startLoading,
            stopLoading,
            displayToast
          );
          if (data) {
            const courseIdResolved =
              data.course?.id ??
              data.courseId ??
              (typeof data.course === "number" || typeof data.course === "string"
                ? data.course
                : data.course?.value ?? "") ??
              courseId ??
              "";

            if (data.course && typeof data.course === "object" && data.course.id) {
              if (!fetchedCourses.some((c) => String(c.id) === String(data.course.id))) {
                fetchedCourses.push(data.course);
                setCourses([...fetchedCourses]);
              }
            }

            let associatedCourse = fetchedCourses?.find(
              (c) => String(c.id) === String(courseIdResolved)
            );

            // Directly fetch course by ID if not already present
            if (!associatedCourse && courseIdResolved) {
              try {
                const singleCourse = await apiCall(
                  `/courses/${courseIdResolved}`,
                  useMockData,
                  "GET",
                  { id: courseIdResolved }
                );
                if (singleCourse) {
                  associatedCourse = singleCourse;
                  if (!fetchedCourses.some((c) => String(c.id) === String(singleCourse.id))) {
                    fetchedCourses.push(singleCourse);
                    setCourses([...fetchedCourses]);
                  }
                }
              } catch (err) {
                console.warn("Failed to fetch course by ID:", courseIdResolved, err);
              }
            }

            const difficultyArray = Array.isArray(data.difficulty)
              ? data.difficulty
              : typeof data.difficulty === "string"
              ? data.difficulty
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : [];
            const chapterArray = Array.isArray(data.chapter)
              ? data.chapter.map((ch) => toValue(ch))
              : typeof data.chapter === "string"
              ? data.chapter
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              : data.chapter
              ? [toValue(data.chapter)]
              : [];
            // Convert subject and classStandard to codes for proper filtering
            const subjectCode =
              getCodeFromId(toValue(data.subject), subjects) ||
              (associatedCourse ? getCodeFromId(associatedCourse.subject, subjects) : "");
            const classCode =
              getCodeFromId(toValue(data.classStandard), academicClasses) ||
              (associatedCourse
                ? getCodeFromId(associatedCourse.academicClass, academicClasses)
                : "");
            const curriculumVal =
              toValue(data.curriculum) || associatedCourse?.curriculum || "";

            const tagsArray = typeof data.tagIds === "string"
              ? data.tagIds.split(",").map((s) => s.trim()).filter(Boolean)
              : Array.isArray(data.tagIds)
              ? data.tagIds.map((v) => String(v))
              : [];
            const questionTypesArray = Array.isArray(data.questionTypes)
              ? data.questionTypes.map((q) => toValue(q))
              : typeof data.questionTypes === "string"
              ? data.questionTypes.split(",").map((s) => s.trim()).filter(Boolean)
              : [];
            setEvaluation({
              name: data.name ?? "",
              courseValue: courseIdResolved ? String(courseIdResolved) : "",
              subjectValue: subjectCode,
              chapterValue: chapterArray,
              curriculumValue: curriculumVal,
              classStandardValue: classCode,
              difficulty: difficultyArray,
              questionTypes: questionTypesArray,
              tags: tagsArray,
              conceptTopic: data.conceptTopic ?? "",
              numberOfQuestions: String(data.numberOfQuestions ?? ""),
              maxTimeMinutes: String(data.maxTimeMinutes ?? ""),
              expiryDateTime: data.expiryDateTime?.split("T")[0] ?? "",
              isPractice: !!data.isPractice,
              isActive: data.isActive !== undefined ? !!data.isActive : true,
            });
          } else {
            displayToast(
              `Evaluation with ID ${evaluationId} not found.`,
              "error"
            );
          }
        } catch (err) {
          console.error("Failed to fetch evaluation:", err);
          displayToast(`Error: ${err.message}`, "error");
        }
      } else if (courseId) {
        // Pre-populate fields if a courseId is provided for a new evaluation
        let preselectedCourse = fetchedCourses.find(
          (c) => String(c.id) === String(courseId)
        );
        if (!preselectedCourse) {
          try {
            const singleCourse = await apiCall(
              `/courses/${courseId}`,
              useMockData,
              "GET",
              { id: courseId }
            );
            if (singleCourse) {
              preselectedCourse = singleCourse;
              if (!fetchedCourses.some((c) => String(c.id) === String(singleCourse.id))) {
                fetchedCourses.push(singleCourse);
                setCourses([...fetchedCourses]);
              }
            }
          } catch (err) {
            console.warn("Failed to fetch course by ID for new evaluation:", courseId, err);
          }
        }
        if (preselectedCourse) {
          // Convert IDs to codes if needed for proper filtering
          // Note: This runs after masterData is loaded, so academicClasses and subjects should be available
          const classCode = getCodeFromId(
            preselectedCourse.academicClass,
            academicClasses
          );
          const subjectCode = getCodeFromId(
            preselectedCourse.subject,
            subjects
          );
          setEvaluation((prev) => ({
            ...prev,
            courseValue: preselectedCourse.id,
            curriculumValue: preselectedCourse.curriculum,
            classStandardValue: classCode,
            subjectValue: subjectCode,
            chapterValue: [], // Reset chapter for a new form
          }));
        } else {
          displayToast(`Course with ID ${courseId} not found.`, "error");
        }
      }

      setFormLoading(false);
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100);
    };

    initializeForm();
  }, [evaluationId, courseId, useMockData]);

  useEffect(() => {
    if (isInitialLoadRef.current) return;
    setEvaluation((prev) => ({ ...prev, chapterValue: [] }));
  }, [evaluation.subjectValue, evaluation.classStandardValue]);

  const filteredChapters = useMemo(() => {
    if (!chapters || !evaluation.subjectValue || !evaluation.classStandardValue)
      return [];
    const filtered = chapters.filter(
      (ch) =>
        ch.subjectValue === evaluation.subjectValue &&
        ch.classStandardValue === evaluation.classStandardValue
    );
    console.log("Filtered chapters:", {
      totalChapters: chapters.length,
      subjectValue: evaluation.subjectValue,
      classStandardValue: evaluation.classStandardValue,
      filteredCount: filtered.length,
      filtered: filtered,
      sampleChapter: chapters[0],
    });
    return filtered;
  }, [chapters, evaluation.subjectValue, evaluation.classStandardValue]);

  useEffect(() => {
    if (
      evaluation.subjectValue &&
      evaluation.classStandardValue &&
      !masterDataLoading
    ) {
      setChaptersError(
        filteredChapters.length
          ? null
          : t("No chapters for selected class and subject")
      );
    } else {
      setChaptersError(null);
    }
  }, [
    evaluation.subjectValue,
    evaluation.classStandardValue,
    masterDataLoading,
    filteredChapters,
    t,
  ]);

  const handleChange = (e) => {
    const { name, value, type, checked, multiple, options } = e.target;

    // This block now only runs if the course dropdown is not disabled by courseId
    if (name === "courseValue") {
      const selectedCourse = courses.find(
        (course) => String(course.id) === value
      );
      // Convert IDs to codes if needed for proper filtering
      const classCode = getCodeFromId(
        selectedCourse?.academicClass,
        academicClasses
      );
      const subjectCode = getCodeFromId(selectedCourse?.subject, subjects);
      setEvaluation((prev) => ({
        ...prev,
        courseValue: value,
        curriculumValue: selectedCourse?.curriculum || "",
        classStandardValue: classCode,
        subjectValue: subjectCode,
        chapterValue: [],
      }));
      return;
    }

    if (multiple) {
      const selectedValues = Array.from(options)
        .filter((opt) => opt.selected)
        .map((opt) => opt.value);
      setEvaluation((prev) => ({ ...prev, [name]: selectedValues }));
    } else {
      setEvaluation((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate expiry date should be today or later
    try {
      const selected = evaluation.expiryDateTime;
      if (!selected || String(selected) < String(todayLocalISO)) {
        displayToast("Expiry date must be today or later", "error");
        return;
      }
    } catch {}
    setFormLoading(true);

    const payload = {
      name: evaluation.name || "",
      ...(Array.isArray(evaluation.chapterValue) &&
      evaluation.chapterValue.length > 0
        ? { chapter: evaluation.chapterValue.join(",") }
        : { chapter: null }),
      conceptTopic: evaluation.conceptTopic || "",
      ...(Array.isArray(evaluation.difficulty) &&
      evaluation.difficulty.length > 0
        ? { difficulty: evaluation.difficulty.join(",") }
        : {}),
      ...(Array.isArray(evaluation.questionTypes) &&
      evaluation.questionTypes.length > 0
        ? { questionTypes: evaluation.questionTypes.join(",") }
        : {}),
      ...(Array.isArray(evaluation.tags) && evaluation.tags.length > 0
        ? { tagIds: evaluation.tags.join(",") }
        : {}),
      numberOfQuestions: parseInt(evaluation.numberOfQuestions, 10),
      maxTimeMinutes: parseInt(evaluation.maxTimeMinutes, 10),
      durationSeconds:
        parseInt(evaluation.maxTimeMinutes, 10) > 0
          ? parseInt(evaluation.maxTimeMinutes, 10) * 60
          : null,
      expiryDateTime: evaluation.expiryDateTime
        ? new Date(evaluation.expiryDateTime).toISOString()
        : null,
      isPractice: !!evaluation.isPractice,
      isActive: true, // Modified evaluations are saved as active so they can be reused
      isArchived: false,
      status: "ACTIVE",
      course: courses.find((c) => String(c.id) === String(evaluation.courseValue)) || {
        id: evaluation.courseValue ? (isNaN(evaluation.courseValue) ? evaluation.courseValue : Number(evaluation.courseValue)) : null,
      },
      createdBy: userId,
      createdDate: new Date().toISOString(),
    };

    try {
      if (evaluationId) {
        await apiCall(
          `/evaluations/${evaluationId}`,
          useMockData,
          "PUT",
          { ...payload, id: evaluationId },
          startLoading,
          stopLoading,
          displayToast
        );
        displayToast(t("evaluationUpdatedSuccessfully"), "success");
      } else {
        await apiCall(
          "/evaluations",
          useMockData,
          "POST",
          payload,
          startLoading,
          stopLoading,
          displayToast
        );
        displayToast(t("evaluationCreatedSuccessfully"), "success");
      }
      navigateTo("evaluations");
    } catch (err) {
      console.error("Failed to save evaluation:", err);
      displayToast(`Error: ${err.message}`, "error");
    } finally {
      setFormLoading(false);
    }
  };

  const overallLoading = formLoading || masterDataLoading;

  return (
    <div className="p-8 md:px-10">
      <ContentHeaderSection
        title={evaluationId ? t("updateEvaluation") : t("createEvaluation")}
        subtitle={t("createOrUpdateEvaluation")}
      />
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 max-w-2xl mx-auto">
        {overallLoading ? (
          <div className="text-center text-gray-600 dark:text-gray-400">
            {t("loadingEvaluationDetails")}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Course Dropdown (now disabled if courseId prop exists) */}
            <div>
              <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                {t("Course")}
              </label>
              <select
                name="courseValue"
                value={evaluation.courseValue}
                onChange={handleChange}
                disabled={(!evaluationId && !!courseId) || masterDataLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              >
                <option value="">
                  {masterDataLoading ? t("loading") : t("select")}
                </option>
                {(courses ?? []).map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.courseName}
                  </option>
                ))}
              </select>
            </div>

            {/* Curriculum Dropdown (Auto-populated and disabled if courseId exists) */}
            <div>
              <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                {t("curriculum")}
              </label>
              <select
                name="curriculumValue"
                value={evaluation.curriculumValue}
                onChange={handleChange}
                disabled={
                  !!courseId || !!evaluation.courseValue || masterDataLoading
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              >
                <option value="">
                  {masterDataLoading ? t("loading") : t("select")}
                </option>
                {(curricula ?? []).map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label ?? item.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Academic Class Dropdown (Auto-populated and disabled if courseId exists) */}
            <div>
              <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                {t("class")}
              </label>
              <select
                name="classStandardValue"
                value={evaluation.classStandardValue}
                onChange={handleChange}
                disabled={
                  !!courseId || !!evaluation.courseValue || masterDataLoading
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              >
                <option value="">
                  {masterDataLoading ? t("loading") : t("select")}
                </option>
                {(academicClasses ?? []).map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label ?? item.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject Dropdown (Auto-populated and disabled if courseId exists) */}
            <div>
              <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                {t("subject")}
              </label>
              <select
                name="subjectValue"
                value={evaluation.subjectValue}
                onChange={handleChange}
                disabled={
                  !!courseId || !!evaluation.courseValue || masterDataLoading
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              >
                <option value="">
                  {masterDataLoading ? t("loading") : t("select")}
                </option>
                {(subjects ?? []).map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label ?? item.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Chapter Dropdown (Filtered by Class and Subject - Multi-select) */}
            <div>
              <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                {t("Chapter")}
              </label>
              <select
                name="chapterValue"
                multiple
                value={evaluation.chapterValue}
                onChange={handleChange}
                disabled={
                  !evaluation.subjectValue ||
                  !evaluation.classStandardValue ||
                  masterDataLoading
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 min-h-[120px]"
                required
              >
                {filteredChapters.length === 0 ? (
                  <option disabled value="">
                    {evaluation.subjectValue && evaluation.classStandardValue
                      ? t("No chapters available")
                      : t("Please select class and subject first")}
                  </option>
                ) : (
                  filteredChapters.map((ch) => (
                    <option key={ch.value || ch.id} value={ch.value || ch.code}>
                      {ch.label || ch.name || ch.value || "Unnamed Chapter"}
                    </option>
                  ))
                )}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t("Hold Ctrl/Cmd to select multiple chapters")}
              </p>
              {chaptersError && (
                <div className="text-red-500 text-xs mt-1">{chaptersError}</div>
              )}
            </div>

            {/* Source field removed as per requirement */}

            {/* Concept/Topic */}
            {/* Question Types (Multi-select) */}
            <div>
              <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                {t("Question Types")}
              </label>
              <select
                name="questionTypes"
                multiple
                value={evaluation.questionTypes}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 min-h-[120px]"
              >
                {(questionTypes ?? []).map((qt) => (
                  <option key={qt.value} value={qt.value}>
                    {qt.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t("Hold Ctrl/Cmd to select multiple types")}
              </p>
            </div>

            {/* Tags (Multi-select) */}
            <div>
              <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                {t("Tags")}
              </label>
              <select
                name="tags"
                multiple
                value={evaluation.tags}
                onChange={handleChange}
                disabled={masterDataLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 min-h-[120px]"
              >
                {(tags ?? []).map((tg) => (
                  <option key={tg.value} value={tg.value}>
                    {tg.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t("Hold Ctrl/Cmd to select multiple tags")}
              </p>
            </div>

            {/* Concept/Topic */}
            <div>
              <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                {t("Concept Topic")}
              </label>
              <input
                type="text"
                name="conceptTopic"
                value={evaluation.conceptTopic}
                onChange={handleChange}
                placeholder={t("enterConceptTopic")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
            </div>

            {/* Number of Questions */}
            <div>
              <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                {t("Number of Questions")}
              </label>
              <input
                type="number"
                name="numberOfQuestions"
                value={evaluation.numberOfQuestions}
                onChange={handleChange}
                required
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
            </div>

            {/* Difficulty (Multi-select) */}
            <div>
              <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                {t("Difficulty Level(s)")}
              </label>
              <select
                name="difficulty"
                multiple
                value={evaluation.difficulty}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 min-h-[120px]"
              >
                <option value="LOW">{t("Low")}</option>
                <option value="MED">{t("Medium")}</option>
                <option value="HIGH">{t("High")}</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t("Hold Ctrl/Cmd to select multiple")}
              </p>
            </div>

            {/* Max Time & Duration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                  {t("Max Time (minutes)")}
                </label>
                <input
                  type="number"
                  name="maxTimeMinutes"
                  value={evaluation.maxTimeMinutes}
                  onChange={handleChange}
                  required
                  min="1"
                  placeholder="30"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t("maxTimeDescription")}
                </p>
              </div>
              {/* <div>
                <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                  {t("Duration (seconds)")}
                </label>
                <input
                  type="number"
                  name="durationSeconds"
                  value={evaluation.durationSeconds}
                  onChange={handleChange}
                  min="1"
                  placeholder="1800"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t("durationDescription")}
                </p>
              </div> */}
            </div>

            {/* Expiry Date */}
            <div>
              <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                {t("Expiry Date")}
              </label>
              <input
                type="date"
                name="expiryDateTime"
                value={evaluation.expiryDateTime}
                onChange={handleChange}
                required
                min={todayLocalISO}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
            </div>

            {/* Practice / Active */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPractice"
                name="isPractice"
                checked={evaluation.isPractice}
                onChange={handleChange}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
              />
              <label
                htmlFor="isPractice"
                className="ml-2 block text-sm text-gray-900 dark:text-gray-300"
              >
                {t("Is Practice Evaluation")}
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                name="isActive"
                checked={evaluation.isActive}
                onChange={handleChange}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
              />
              <label
                htmlFor="isActive"
                className="ml-2 block text-sm text-gray-900 dark:text-gray-300"
              >
                {t("Active")}
              </label>
            </div>
            {/* Evaluation Name Field */}
            <div>
              <label className="block mb-1 font-medium text-gray-700 dark:text-gray-300">
                {t("Evaluation Name")}
              </label>
              <input
                type="text"
                name="name"
                value={evaluation.name}
                onChange={handleChange}
                placeholder={t("enterEvaluationName")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>
            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => navigateTo("evaluations")}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                {t("cancel")}
              </button>
              {evaluationId ? (
                <button
                  type="submit"
                  disabled={overallLoading}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {overallLoading ? t("saving") : t("update")}
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={overallLoading}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {overallLoading ? t("saving") : t("create")}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateUpdateEvaluationForm;
